import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

export function AuthCallback() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    handleCallback();
  }, []);

  const handleCallback = async () => {
    try {
      // Pegar o hash da URL (Supabase retorna tokens no hash)
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const errorParam = hashParams.get('error');
      const errorDescription = hashParams.get('error_description');

      // Verificar se há erro
      if (errorParam) {
        console.error('Auth error:', errorParam, errorDescription);
        setError(errorDescription || 'Erro ao fazer login');
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      // Se não há tokens, verificar query params também
      const queryParams = new URLSearchParams(window.location.search);
      const queryError = queryParams.get('error');
      const queryErrorDescription = queryParams.get('error_description');

      if (queryError) {
        console.error('Auth error:', queryError, queryErrorDescription);
        setError(queryErrorDescription || 'Erro ao fazer login');
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      // Verificar sessão atual
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError) {
        console.error('Session error:', sessionError);
        setError('Erro ao verificar sessão');
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      if (!session) {
        console.log('No session found, redirecting to login');
        navigate('/login');
        return;
      }

      // Verificar se usuário existe e está ativo
      const { data: user, error: userError } = await supabase
        .from('users')
        .select('is_active, is_admin, org_id')
        .eq('id', session.user.id)
        .single();

      if (userError) {
        console.error('Error checking user status:', userError);
        // Se não encontrou usuário, pode ser primeiro acesso
        setError('Aguardando criação do perfil...');
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      // Verificar se está ativo
      if (!user.is_active) {
        setError('Seu acesso está aguardando aprovação de um administrador. Você receberá um email quando for aprovado.');
        setTimeout(() => {
          supabase.auth.signOut();
          navigate('/login');
        }, 5000);
        return;
      }

      // Verificar se tem org_id
      if (!user.org_id) {
        setError('Aguardando atribuição de organização. Entre em contato com o administrador.');
        setTimeout(() => {
          supabase.auth.signOut();
          navigate('/login');
        }, 5000);
        return;
      }

      // Usuário aprovado, redirecionar para dashboard
      console.log('User approved, redirecting to dashboard');
      navigate('/dashboard');

    } catch (err) {
      console.error('Callback error:', err);
      setError('Erro inesperado ao processar login');
      setTimeout(() => navigate('/login'), 3000);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted">
      <div className="max-w-md w-full p-8 bg-background rounded-sm shadow-md text-center">
        {error ? (
          <>
            <div className="mb-4">
              <svg
                className="mx-auto h-12 w-12 text-destructive"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-foreground mb-2">
              {error.includes('aguardando aprovação') ? 'Aguardando Aprovação' : 'Erro no Login'}
            </h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <p className="text-sm text-muted-foreground">Redirecionando...</p>
          </>
        ) : (
          <>
            <div className="mb-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            </div>
            <h2 className="text-xl font-semibold text-foreground mb-2">
              Processando login...
            </h2>
            <p className="text-muted-foreground">Aguarde enquanto verificamos suas credenciais</p>
          </>
        )}
      </div>
    </div>
  );
}
